import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFont } from '../../contexts/FontContext';
import { getFrameUIProperties } from '../../utils/frameStyleUtils';

/**
 * Helper to convert hex color to rgba
 */
const hexToRgba = (color, opacity) => {
  if (!color || opacity === undefined) return 'rgba(128, 128, 128, 0.9)';
  if (color.startsWith('rgba') || color.startsWith('rgb')) return color;
  
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * GameStartSequence - Minimal loading + countdown component for all games
 * 
 * @param {Object} props
 * @param {boolean} props.isLoading - Whether assets are still loading
 * @param {number} props.countdown - Current countdown value (3, 2, 1, 0)
 * @param {boolean} props.isVisible - Whether to show the overlay
 * @param {Object} props.colors - Theme colors
 * @param {string} [props.font] - Font family
 * @param {string} [props.instructions] - Game instructions to display below countdown
 */
const GameStartSequence = ({
  isLoading,
  countdown,
  isVisible,
  colors = {},
  font,
  fontScale: fontScaleProp, // Custom font scale from settings (optional)
  frameStyle = 'clean', // Frame style for consistent UI styling
  instructions,
}) => {
  const { loadFont } = useFont();
  const surfaceColor = colors.surface || '#FFFFFF';
  const primaryColor = colors.primaryAction || colors.primary || '#7C3AED';
  const textColor = colors.text || '#4B5563';
  
  // Get frame style UI properties
  const uiProps = getFrameUIProperties(frameStyle);

  // Load font using centralized font loader
  useEffect(() => {
    if (font) {
      loadFont(font);
    }
  }, [font, loadFont]);

  // Properly quote font names for CSS
  const fontFamily = font 
    ? `"${font}", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
    : 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  // Don't show anything if not visible
  if (!isVisible) return null;

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          key="start-sequence"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none"
        >
          <AnimatePresence mode="wait">
            {isLoading ? (
              /* Loading Phase - Subtle pulsing dots */
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex gap-2"
              >
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.4, 1, 0.4],
                    }}
                    transition={{
                      duration: 0.8,
                      repeat: Infinity,
                      delay: i * 0.15,
                      ease: "easeInOut",
                    }}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: uiProps.isPixelated ? '0' : '50%',
                      backgroundColor: primaryColor,
                    }}
                  />
                ))}
              </motion.div>
            ) : countdown > 0 ? (
              /* Countdown Phase - Clean number display with instructions */
              /* Both countdown and instructions wrapped together so they exit at exactly the same time */
              <motion.div
                key="countdown-container"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col items-center justify-center gap-4"
              >
                {/* Countdown Circle - animates on number change */}
                <motion.div
                  key={`countdown-${countdown}`}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 1.1, opacity: 0 }}
                  transition={{ 
                    type: 'spring',
                    stiffness: 400,
                    damping: 25,
                    duration: 0.3
                  }}
                  className="flex items-center justify-center"
                >
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '72px',
                      height: '72px',
                      backgroundColor: hexToRgba(surfaceColor, 0.9),
                      border: `2px solid ${primaryColor}`,
                      boxShadow: uiProps.isPixelated 
                        ? `4px 4px 0 rgba(0, 0, 0, 0.2)` 
                        : '0 4px 20px rgba(0, 0, 0, 0.1)',
                      borderRadius: uiProps.isPixelated ? '0' : uiProps.borderRadius,
                    }}
                  >
                    <span
                      className="text-5xl md:text-6xl font-semibold leading-none"
                      style={{ color: textColor, fontFamily }}
                    >
                      {countdown}
                    </span>
                  </div>
                </motion.div>
                
                {/* Instructions - surface pill background for guaranteed contrast.
                    Animated in with a matching spring + small delay so it doesn't
                    hard-appear under the bouncing countdown number. */}
                {instructions && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 22, delay: 0.12 }}
                    className="text-center"
                    style={{
                      maxWidth: '280px',
                      backgroundColor: hexToRgba(surfaceColor, 0.85),
                      border: `1px solid ${hexToRgba(primaryColor, 0.3)}`,
                      borderRadius: uiProps.isPixelated ? '0' : '9999px',
                      padding: '6px 16px',
                      boxShadow: uiProps.isPixelated
                        ? '2px 2px 0 rgba(0, 0, 0, 0.15)'
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <span
                      className="text-sm md:text-base font-semibold leading-snug"
                      style={{
                        color: textColor,
                        fontFamily,
                      }}
                    >
                      {instructions}
                    </span>
                  </motion.div>
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GameStartSequence;
